<template>
  <div class="page-container" style="min-height: auto;">
    <Toast ref="toast" />

    <div class="page-title-container">
      <h1 class="title-text">Admin Create Site</h1>
    </div>

    <div class="center-items">
      <button class="primary-button" @click="$router.push({ path: '/admin' })">⬅️ Back to Admin</button>
    </div>

    <div class="feature admin-dashboard-container">
      <b>Name*</b>
      <input class="input-container input-container-medium" v-model="form.name" type="text" />
      <div v-if="errors.missingName">Missing name</div>
      <div v-if="warn.showNoValidSpecialCharacter">
        <div>
          ⚠️ Can't add special characters to name
        </div>
        <div>| {{ specialCharacters.join(" | ") }} |</div>
      </div>
    </div>
    <div class="feature admin-dashboard-container">
      <b>Url*</b>
      <input class="input-container input-container-medium" placeholder="www.website.com" v-model="form.url" type="text" />
      <div v-if="errors.missingUrl">Missing url</div>
      <div v-if="errors.badFormattedUrl">Bad formatted url</div>
    </div>
    <div class="feature admin-dashboard-container">
      <b>Description*</b>
      <textarea class="input-container text-area-container-medium" v-model="form.description"></textarea>
      <div v-if="errors.missingDescription">Missing description</div>
    </div>
    <div class="feature admin-dashboard-container">
      <b>Niche*</b>
      <input class="input-container input-container-medium" v-model="form.niche" type="text" />
      <div v-if="errors.missingNiche">Missing niche</div>
    </div>
    <div class="feature admin-dashboard-container">
      <b>Country*</b>
      <select class="input-container input-container-medium" v-model="form.country">
        <option :value="c" :key="c" v-for="c in status.countries">
          {{ c }}
        </option>
      </select>
    </div>
    <div class="feature admin-dashboard-container">
      <b>Enabled</b>
      <input v-model="form.enabled" type="checkbox" />
    </div>
    <div class="feature admin-dashboard-container">
      <b>Region</b>
      <input class="input-container input-container-medium" v-model="form.region" type="text" />
    </div>
    <div class="feature admin-dashboard-container">
      <b>Logo*</b>
      <img class="logo" :src="form.preview" alt="preview" />
      <input ref="logo" type="file" @change="() => filePreview($refs.logo)" />
      <div v-if="errors.missingLogo">Missing logo</div>
    </div>
    <div class="feature admin-dashboard-container">
      <b>Tags</b>
      <input class="input-container input-container-medium" v-model="form.tags" type="text" />
      <div class="tag" v-for="t in form.tags.split(',')" :key="t">
        {{ t }}
      </div>
    </div>
    <div class="feature admin-dashboard-container">
      <b>Has user number?</b>
      <input v-model="form.hasUserNumber" type="checkbox" />
    </div>
    <div class="feature admin-dashboard-container">
      <b>User number</b>
      <input class="input-container input-container-medium" :disabled="!form.hasUserNumber" v-model="form.userNumber" type="number" />
    </div>

    <div class="feature admin-dashboard-container">
      <b>Type</b>
      <select class="input-container input-container-medium" style="margin-right:0rem;" v-model="form.type">
        <option v-for="(t, i) in Object.values(types)" :key="`types-${i}`">{{ t }}</option>
      </select>

      <div v-if="form.type === types.hasPrice">
        <b>Min Price</b>
        <input class="input-container input-container-medium" v-model="form.minPrice" type="number" />
      </div>
      <div v-if="errors.missingMinPrice">Missing min price</div>

      <div v-if="form.type === types.isLimited">
        <b>Limited reason</b>
        <input class="input-container input-container-medium" v-model="form.limitedReason" type="text" />
      </div>
      <div v-if="errors.missingLimitedReason">Missing limited reason</div>
    </div>

    <button class="primary-button" style="margin-left: 0.5rem; margin-bottom: 3rem;" @click="upload">Upload Site</button>
  </div>
</template>

<script>
import Toast from "@/components/Toast";

import image from "@/services/image";
import firebase from "@/firebase";
import utils from "@/utils";
import types from "@/types";

export default {
  components: {
    Toast,
  },

  created() {
    window.scrollTo(0, 0);
  },

  watch: {
    "form.name"(val) {
      const filtered = this.specialCharacters.filter((s) => val.indexOf(s) !== -1);
      if (filtered.length > 0) {
        for (const s of this.specialCharacters) {
          this.form.name = this.form.name.replaceAll(s, "");
        }
        this.warn.showNoValidSpecialCharacter = true;
      }
    },
  },

  data() {
    return {
      specialCharacters: [";", "/", "?", ":", "@", "&", "=", "+", "$", ",", "-"],
      types: types.sitesType,
      form: {
        type: types.sitesType.jobboardyReady,
        name: "",
        url: "",
        description: "",
        country: "🏡 Remote",
        niche: "",
        region: "",
        preview: "",
        enabled: true,
        tags: "",
        minPrice: 0,
        limitedReason: "",
        userNumber: 0,
        hasUserNumber: false,
      },
      warn: {
        showNoValidSpecialCharacter: false,
      },
      errors: {
        missingName: false,
        missingUrl: false,
        badFormattedUrl: false,
        missingDescription: false,
        missingLogo: false,
        missingNiche: false,
        serverError: false,
      },
      status: {
        countries: utils.countryNames,
      },
    };
  },

  methods: {
    async filePreview(ref) {
      this.form.preview = "";
      const response = await image.readImage(ref.files[0]);
      if (response.data != null) {
        this.form.preview = response.data;
      }
    },

    validate() {
      this.errors.missingName = false;
      this.errors.missingUrl = false;
      this.errors.badFormattedUrl = false;
      this.errors.missingDescription = false;
      this.errors.missingLogo = false;
      this.errors.missingNiche = false;
      this.errors.serverError = false;
      this.errors.missingMinPrice = false;
      this.errors.missingLimitedReason = false;

      let error = false;

      if (this.form.name === "" || typeof this.form.name !== "string") {
        this.errors.missingName = true;
        error = true;
      }

      if (this.form.url === "" || typeof this.form.url !== "string") {
        this.errors.missingUrl = true;
        error = true;
      } else {
        if (!utils.validURL(this.form.url)) {
          this.errors.badFormattedUrl = true;
          error = true;
        }
      }

      if (this.form.description === "" || typeof this.form.description !== "string") {
        this.errors.missingDescription = true;
        error = true;
      }

      if (this.form.niche === "" || typeof this.form.niche !== "string") {
        this.errors.missingNiche = true;
        error = true;
      }

      if (this.form.preview === "" || typeof this.form.preview !== "string") {
        this.errors.missingLogo = true;
        error = true;
      }

      if (this.form.type === this.types.hasPrice) {
        if (this.form.minPrice === "" || typeof this.form.minPrice !== "string") {
          this.errors.missingMinPrice = true;
          error = true;
        }
      }

      if (this.form.type === this.types.isLimited) {
        if (this.form.limitedReason === "" || typeof this.form.limitedReason !== "string") {
          this.errors.missingLimitedReason = true;
          error = true;
        }
      }

      return !error;
    },

    async uploadImage(id) {
      const imagePath = `images/site-logos/${id}.png`;
      const ref = firebase.storage.ref().child(imagePath);
      await ref.put(this.$refs.logo.files[0]);
    },

    async upload() {
      if (!this.validate()) {
        console.error("not valid");
        this.$refs.toast.showError("Mising fields");
        return;
      }

      const data = {
        name: this.form.name,
        url: this.form.url,
        region: this.form.region,
        description: this.form.description,
        logoPath: `images/site-logos/${this.form.name}.png`,
        niche: this.form.niche,
        country: this.form.country,
        enabled: this.form.enabled,
        tags: this.form.tags.trim().split(","),
        hasUserNumber: this.form.hasUserNumber,
        userNumber: this.form.userNumber,
        postingType: this.form.type,
        minPrice: this.form.minPrice,
        limitedReason: this.form.limitedReason,
      };

      try {
        await firebase.functions.httpsCallable("createSite")(data);
        await this.uploadImage(this.form.name);
        this.$refs.toast.show("Success!");
      } catch (e) {
        console.error(e.message);
        this.errors.serverError = true;
        this.$refs.toast.showError(e.message);
      } finally {
        this.form.name = "";
        this.form.url = "";
        this.form.description = "";
        this.form.preview = "";
        this.form.niche = "";
        this.form.region = "";
        this.form.country = "🏡 Remote";
        this.form.enabled = true;
        this.form.tags = "";
        this.form.limitedReason = "";
        this.form.hasUserNumber = false;
        this.form.userNumber = 0;
        this.form.minPrice = "";
        this.form.type = types.sitesType.jobboardyReady;
      }
    },
  },
};
</script>

<style scoped>
.logo {
  width: 40px;
  height: 40px;
}
</style>
